// =============================================
// 2. FILTERS PANEL COMPONENT
// components/reports/FiltersPanel.js
// =============================================

import React, { useState, useEffect } from 'react'
import { Filter, X, CalendarDays, Building2, FolderOpen, Users } from 'lucide-react'
import { supabase } from '../../lib/supabase'

export const FiltersPanel = ({ filters, onChange, loading }) => {
  const [showFilters, setShowFilters] = useState(false)
  const [clients, setClients] = useState([])
  const [projects, setProjects] = useState([])
  const [users, setUsers] = useState([])

  // Load filter options
  useEffect(() => {
    loadFilterOptions()
  }, [])

  const loadFilterOptions = async () => {
    try {
      // Load clients, projects, users for filter dropdowns
      const [clientsRes, projectsRes, usersRes] = await Promise.all([
        supabase.from('clients').select('id, name').eq('status', 'active'),
        supabase.from('projects').select('id, name').eq('status', 'active'), 
        supabase.from('user_profiles').select('id, full_name, email')
      ])

      setClients(clientsRes.data || [])
      setProjects(projectsRes.data || [])
      setUsers(usersRes.data || [])
    } catch (error) {
      console.error('Error loading filter options:', error)
    }
  }

  const handleFilterChange = (key, value) => {
    onChange({ ...filters, [key]: value })
  }

  const clearAllFilters = () => {
    onChange({
      startDate: '',
      endDate: '',
      clientId: 'all',
      projectId: 'all',
      userId: 'all',
      status: 'all',
      billableOnly: false
    })
  }

  const hasActiveFilters = Object.values(filters).some(value => 
    value && value !== 'all' && value !== false
  )

  return (
    <div className="bg-white rounded-xl shadow-sm border border-slate-200">
      <div className="p-4 border-b border-slate-200">
        <button
          onClick={() => setShowFilters(!showFilters)}
          className="flex items-center space-x-2 text-slate-700 hover:text-slate-900 transition-colors"
        >
          <Filter className="w-5 h-5" />
          <span className="font-medium">Filters</span>
          {hasActiveFilters && (
            <span className="bg-blue-100 text-blue-800 text-xs px-2 py-1 rounded-full">
              Active
            </span>
          )}
        </button>
      </div>

      {showFilters && (
        <div className="p-6 space-y-6">
          {/* Date Range */}
          <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
            <div>
              <label className="block text-sm font-medium text-slate-700 mb-2">
                <CalendarDays className="w-4 h-4 inline mr-2" />
                Start Date
              </label>
              <input
                type="date"
                value={filters.startDate}
                onChange={(e) => handleFilterChange('startDate', e.target.value)}
                className="w-full px-3 py-2 border border-slate-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
              />
            </div>
            <div>
              <label className="block text-sm font-medium text-slate-700 mb-2">
                <CalendarDays className="w-4 h-4 inline mr-2" />
                End Date
              </label>
              <input
                type="date"
                value={filters.endDate}
                onChange={(e) => handleFilterChange('endDate', e.target.value)}
                className="w-full px-3 py-2 border border-slate-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
              />
            </div>
          </div>

          {/* Dropdowns */}
          <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
            <div>
              <label className="block text-sm font-medium text-slate-700 mb-2">
                <Building2 className="w-4 h-4 inline mr-2" />
                Client
              </label>
              <select
                value={filters.clientId}
                onChange={(e) => handleFilterChange('clientId', e.target.value)}
                className="w-full px-3 py-2 border border-slate-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
              >
                <option value="all">All Clients</option>
                {clients.map(client => (
                  <option key={client.id} value={client.id}>{client.name}</option>
                ))}
              </select>
            </div>

            <div>
              <label className="block text-sm font-medium text-slate-700 mb-2">
                <FolderOpen className="w-4 h-4 inline mr-2" />
                Project
              </label>
              <select
                value={filters.projectId}
                onChange={(e) => handleFilterChange('projectId', e.target.value)}
                className="w-full px-3 py-2 border border-slate-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
              >
                <option value="all">All Projects</option>
                {projects.map(project => (
                  <option key={project.id} value={project.id}>{project.name}</option>
                ))}
              </select>
            </div>

            <div>
              <label className="block text-sm font-medium text-slate-700 mb-2">
                <Users className="w-4 h-4 inline mr-2" />
                Team Member
              </label>
              <select
                value={filters.userId}
                onChange={(e) => handleFilterChange('userId', e.target.value)}
                className="w-full px-3 py-2 border border-slate-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
              >
                <option value="all">All Team Members</option>
                {users.map(user => (
                  <option key={user.id} value={user.id}>
                    {user.full_name || user.email}
                  </option>
                ))}
              </select>
            </div>
          </div>

          {/* Additional Filters */}
          <div className="flex flex-wrap gap-6">
            <div>
              <label className="block text-sm font-medium text-slate-700 mb-2">Status</label>
              <select
                value={filters.status}
                onChange={(e) => handleFilterChange('status', e.target.value)}
                className="px-3 py-2 border border-slate-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
              >
                <option value="all">All Status</option>
                <option value="draft">Draft</option>
                <option value="submitted">Submitted</option>
                <option value="approved">Approved</option>
                <option value="rejected">Rejected</option>
              </select>
            </div>

            <div className="flex items-end">
              <label className="flex items-center space-x-2">
                <input
                  type="checkbox"
                  checked={filters.billableOnly}
                  onChange={(e) => handleFilterChange('billableOnly', e.target.checked)}
                  className="w-4 h-4 text-blue-600 rounded focus:ring-blue-500"
                />
                <span className="text-sm font-medium text-slate-700">Billable Only</span>
              </label>
            </div>
          </div>

          {/* Clear Filters */}
          {hasActiveFilters && (
            <div className="flex justify-end pt-4 border-t border-slate-200">
              <button
                onClick={clearAllFilters}
                className="flex items-center space-x-2 text-slate-600 hover:text-slate-800 transition-colors"
              >
                <X className="w-4 h-4" />
                <span>Clear All Filters</span>
              </button>
            </div>
          )}
        </div>
      )}
    </div>
  )
}

export default FiltersPanel